import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { User } from 'firebase/auth';
import type { Database } from './database.types';
import { DatabaseIdentityController } from './databaseIdentity';
import { getTurnstileToken } from './turnstile';
import { principalFromAccessToken, scopedDatabaseToken } from './databasePrincipal';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

if (typeof window === 'undefined') {
  throw new Error('Supabase browser clients require window.sessionStorage');
}

const guestAuthClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: window.sessionStorage,
    storageKey: 'doganju-guest-auth',
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
});

const databaseIdentity = new DatabaseIdentityController(
  guestAuthClient,
  () => getTurnstileToken(import.meta.env.VITE_TURNSTILE_SITE_KEY)
);

export async function synchronizeDatabasePrincipal(user: User | null): Promise<void> {
  await databaseIdentity.synchronize(user);
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  accessToken: () => databaseIdentity.getAccessToken(),
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
});

export interface DatabaseScope {
  principal: string;
  client: SupabaseClient<Database>;
}

export async function getDatabaseScope(): Promise<DatabaseScope> {
  const principal = principalFromAccessToken(await databaseIdentity.getAccessToken());
  return {
    principal,
    client: createClient<Database>(supabaseUrl, supabaseAnonKey, {
      accessToken: scopedDatabaseToken(principal, () => databaseIdentity.getAccessToken()),
      auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false }
    })
  };
}
